import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export const QUEST_NODE_SIZE = 26;
export const QUEST_CHEST_ICON_SIZE = 16;
export const QUEST_COIN_SIZE = 10;
export const QUEST_CHEST_ARROW_SIZE = 10;
/** Tall enough for three nodes between the header and the chest bar; Streak stretches to match. */
const WEEKLY_MIN_HEIGHT = 190;

/**
 * The "Home Final" design draws Weekly as a football pitch; its green, the translucent markings, the
 * node wells and the chest bar are not theme tokens (nothing else uses them), so they live here.
 */
export const WEEKLY_PITCH_GREEN = '#163B2A';
const PITCH_BORDER = 'rgba(255, 255, 255, 0.14)';
const NODE_ACTIVE_WELL = '#2A2310';
const NODE_PENDING_WELL = '#1A1326';
const NODE_PENDING_BORDER = '#2A2F3A';
const ACTIVE_GLOW = 'rgba(252, 211, 77, 0.35)';
const CHEST_BAR_BG = 'rgba(15, 10, 26, 0.78)';
const TEXT_SHADOW = 'rgba(0, 0, 0, 0.7)';

export const getWeeklyQuestCardStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      flex: 1,
      minWidth: 0,
      minHeight: WEEKLY_MIN_HEIGHT,
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: WEEKLY_PITCH_GREEN,
      borderWidth: 1.5,
      borderColor: PITCH_BORDER,
      borderRadius: theme.borderRadius.xl,
      paddingHorizontal: 10,
      paddingTop: 9,
      paddingBottom: 8,
      gap: 6,
    },
    pitch: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 6,
      zIndex: 1,
    },
    headerText: {
      flex: 1,
      minWidth: 0,
      gap: 1,
    },
    label: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.white,
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      textShadowColor: TEXT_SHADOW,
      textShadowOffset: { width: 0, height: 1 },
      textShadowRadius: 2,
    },
    countdown: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.white,
      fontVariant: ['tabular-nums'],
      textShadowColor: TEXT_SHADOW,
      textShadowOffset: { width: 0, height: 1 },
      textShadowRadius: 2,
    },
    tasks: {
      flex: 1,
      justifyContent: 'space-evenly',
      paddingHorizontal: 4,
      paddingVertical: 2,
      zIndex: 1,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      minHeight: QUEST_NODE_SIZE,
    },
    rowPressed: {
      opacity: 0.75,
    },
    rowHover: {
      opacity: 0.9,
    },
    node: {
      width: QUEST_NODE_SIZE,
      height: QUEST_NODE_SIZE,
      borderRadius: theme.borderRadius.full,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 2,
    },
    nodeDone: {
      backgroundColor: theme.success,
      borderColor: theme.success,
    },
    nodeActive: {
      backgroundColor: NODE_ACTIVE_WELL,
      borderColor: theme.accent,
      shadowColor: ACTIVE_GLOW,
      shadowOpacity: 1,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 0 },
      elevation: 3,
    },
    nodePending: {
      backgroundColor: NODE_PENDING_WELL,
      borderColor: NODE_PENDING_BORDER,
    },
    nodeRatio: {
      fontSize: 9,
      fontWeight: '800',
      fontVariant: ['tabular-nums'],
    },
    nodeRatioActive: {
      color: theme.accent,
    },
    nodeRatioPending: {
      color: theme.gray[500],
    },
    title: {
      flex: 1,
      minWidth: 0,
      fontSize: 11,
      fontWeight: '700',
      lineHeight: 13,
      color: theme.white,
      textShadowColor: TEXT_SHADOW,
      textShadowOffset: { width: 0, height: 1 },
      textShadowRadius: 2,
    },
    titleDone: {
      color: theme.gray[500],
    },
    chestBar: {
      height: 26,
      borderRadius: 7,
      backgroundColor: CHEST_BAR_BG,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
      zIndex: 1,
    },
    chestIcon: {
      width: QUEST_CHEST_ICON_SIZE,
      height: QUEST_CHEST_ICON_SIZE,
    },
    chestText: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.gray[600],
    },
    chestOpened: {
      color: theme.success,
    },
    chestReward: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
    },
    chestRewardText: {
      fontSize: 10,
      fontWeight: '800',
      color: theme.accent,
      fontVariant: ['tabular-nums'],
    },
    coin: {
      width: QUEST_COIN_SIZE,
      height: QUEST_COIN_SIZE,
    },
    emptyText: {
      paddingVertical: 8,
      fontSize: 12,
      color: theme.gray[500],
    },
  });

export type WeeklyQuestCardStyles = ReturnType<typeof getWeeklyQuestCardStyles>;
